<template>
  <div class="pt-control-bar">
    <!-- 상태/타이머 -->
    <div class="pt-control-bar__status">
      <span class="pt-control-bar__dot" :class="dotClass"></span>
      <span class="pt-control-bar__status-text">{{ statusText }}</span>
      <span v-if="elapsedSec >= 0" class="pt-control-bar__timer">{{ formattedElapsed }}</span>
      <span v-if="autoDowngraded" class="pt-control-bar__badge pt-control-bar__badge--warn">
        {{ $t ? $t('자동 신속 모드 전환') : '자동 신속 모드 전환' }}
      </span>
      <span
        v-if="ptMode"
        class="pt-control-bar__mode-badge"
        :class="ptMode === 'live' ? 'pt-control-bar__mode-badge--accurate' : 'pt-control-bar__mode-badge--quick'"
        :title="ptMode === 'live' ? ($t ? $t('정확도 모드: 문맥 분석 후 한꺼번에 통역') : '정확도 모드') : ($t ? $t('신속 모드: 즉시 표시') : '신속 모드')"
      >
        {{ ptMode === 'live' ? ($t ? $t('🎯 정확도') : '🎯 정확도') : ($t ? $t('⚡ 신속') : '⚡ 신속') }}
      </span>
    </div>

    <!-- 컨트롤 버튼 -->
    <div class="pt-control-bar__buttons">
      <button
        v-if="!isStreaming"
        class="pt-btn pt-btn--start"
        @click="$emit('start')"
        :disabled="isConnecting"
      >
        <span class="pt-btn__icon">▶</span>
        <span class="pt-btn__label">{{ isConnecting ? ($t ? $t('연결 중…') : '연결 중…') : ($t ? $t('시작') : '시작') }}</span>
      </button>

      <template v-else>
        <button v-if="!isPaused" class="pt-btn pt-btn--pause" @click="$emit('pause')">
          <span class="pt-btn__icon">⏸</span>
          <span class="pt-btn__label">{{ $t ? $t('일시정지') : '일시정지' }}</span>
        </button>
        <button v-else class="pt-btn pt-btn--resume" @click="$emit('resume')">
          <span class="pt-btn__icon">▶</span>
          <span class="pt-btn__label">{{ $t ? $t('재개') : '재개' }}</span>
        </button>

        <button class="pt-btn pt-btn--stop" @click="$emit('stop')">
          <span class="pt-btn__icon">⏹</span>
          <span class="pt-btn__label">{{ $t ? $t('중지') : '중지' }}</span>
        </button>
      </template>

      <button class="pt-btn pt-btn--reset" @click="$emit('reset')" :disabled="isStreaming">
        <span class="pt-btn__icon">⟲</span>
        <span class="pt-btn__label">{{ $t ? $t('초기화') : '초기화' }}</span>
      </button>
    </div>

    <!-- 보조 버튼 -->
    <div class="pt-control-bar__aux">
      <button
        class="pt-aux-btn pt-aux-btn--toggle"
        @click="$emit('toggle-direction')"
        :title="directionTitle"
      >
        <span>{{ direction === 'newest_top' ? '↑' : '↓' }}</span>
      </button>
      <button class="pt-aux-btn" @click="$emit('settings')" :disabled="isStreaming" :title="$t ? $t('설정') : '설정'">
        <span>⚙</span>
      </button>
      <button class="pt-aux-btn" @click="$emit('history')" :title="$t ? $t('발표 이력') : '발표 이력'">
        <span>📜</span>
      </button>
      <button class="pt-aux-btn" @click="$emit('fullscreen')" :title="$t ? $t('전체화면') : '전체화면'">
        <span>⛶</span>
      </button>
      <button class="pt-aux-btn pt-aux-btn--exit" @click="$emit('exit')" :title="$t ? $t('종료') : '종료'">
        <span>✕</span>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PtControlBar',
  props: {
    isConnecting: { type: Boolean, default: false },
    isStreaming: { type: Boolean, default: false },
    isPaused: { type: Boolean, default: false },
    elapsedSec: { type: Number, default: 0 },
    error: { type: String, default: null },
    autoDowngraded: { type: Boolean, default: false },
    /** 'newest_top' | 'newest_bottom' — 토글 버튼 표시용 */
    direction: { type: String, default: 'newest_top' },
    /** 'stt' | 'live' — 현재 모드 뱃지 */
    ptMode: { type: String, default: 'stt' },
  },
  computed: {
    dotClass: function () {
      if (this.error) return 'pt-control-bar__dot--err'
      if (this.isPaused) return 'pt-control-bar__dot--paused'
      if (this.isStreaming) return 'pt-control-bar__dot--live'
      if (this.isConnecting) return 'pt-control-bar__dot--connecting'
      return 'pt-control-bar__dot--idle'
    },
    statusText: function () {
      if (this.error) return this.error
      if (this.isPaused) return this.$t ? this.$t('일시정지') : '일시정지'
      if (this.isStreaming) return this.$t ? this.$t('LIVE') : 'LIVE'
      if (this.isConnecting) return this.$t ? this.$t('연결 중') : '연결 중'
      return this.$t ? this.$t('대기') : '대기'
    },
    formattedElapsed: function () {
      const total = this.elapsedSec | 0
      const m = Math.floor(total / 60)
      const s = total % 60
      const pad = function (n) { return n < 10 ? '0' + n : '' + n }
      return pad(m) + ':' + pad(s)
    },
    directionTitle: function () {
      if (this.direction === 'newest_top') {
        return this.$t ? this.$t('새 카드를 위에서 등장') : '새 카드를 위에서 등장 (클릭: 아래로 변경)'
      }
      return this.$t ? this.$t('새 카드를 아래에서 등장') : '새 카드를 아래에서 등장 (클릭: 위로 변경)'
    },
  },
}
</script>

<style scoped>
.pt-control-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 16px;
  background: #0B0F1A;
  color: #E5E7EB;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.pt-control-bar__status {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 220px;
}

.pt-control-bar__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #94A3B8;
  display: inline-block;
}
.pt-control-bar__dot--live      { background: #22C55E; box-shadow: 0 0 8px #22C55E; animation: pt-pulse 1.4s infinite; }
.pt-control-bar__dot--connecting{ background: #F59E0B; }
.pt-control-bar__dot--paused    { background: #EAB308; }
.pt-control-bar__dot--err       { background: #EF4444; }
.pt-control-bar__dot--idle      { background: #475569; }

@keyframes pt-pulse {
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.4; }
}

.pt-control-bar__status-text {
  font-weight: 600;
  font-size: 13px;
  letter-spacing: 0.5px;
}

.pt-control-bar__timer {
  font-family: ui-monospace, 'SF Mono', Menlo, monospace;
  font-size: 14px;
  color: #CBD5E1;
}

.pt-control-bar__badge {
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
}
.pt-control-bar__badge--warn { background: rgba(245, 158, 11, 0.2); color: #FCD34D; }

.pt-control-bar__mode-badge {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  font-weight: 700;
  letter-spacing: 0.2px;
}
.pt-control-bar__mode-badge--quick { background: rgba(56, 189, 248, 0.22); color: #BAE6FD; }
.pt-control-bar__mode-badge--accurate { background: rgba(168, 85, 247, 0.25); color: #F3E8FF; }

.pt-control-bar__buttons {
  display: flex;
  gap: 8px;
  flex: 1;
  justify-content: center;
}

.pt-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 14px;
  border: none;
  cursor: pointer;
  color: white;
  background: #334155;
  transition: opacity 0.15s, transform 0.05s;
}
.pt-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.pt-btn:not(:disabled):hover { opacity: 0.92; }
.pt-btn:not(:disabled):active { transform: scale(0.97); }

.pt-btn--start  { background: #22C55E; }
.pt-btn--pause  { background: #EAB308; }
.pt-btn--resume { background: #22C55E; }
.pt-btn--stop   { background: #EF4444; }
.pt-btn--reset  { background: #475569; }

.pt-btn__icon { font-size: 14px; }

.pt-control-bar__aux {
  display: flex;
  gap: 4px;
}

.pt-aux-btn {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: none;
  background: rgba(255, 255, 255, 0.06);
  color: #E5E7EB;
  cursor: pointer;
  font-size: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.pt-aux-btn:hover { background: rgba(255, 255, 255, 0.12); }
.pt-aux-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.pt-aux-btn--exit:hover { background: rgba(239, 68, 68, 0.3); }
</style>
